/**
 * ProfilerManager - Центральний менеджер профілювання UI компонентів системи
 * Відстежує тільки компоненти та шаблони, створені через UIManager
 */

import { action, makeObservable, observable, runInAction } from 'mobx'
import { EventManager } from '../Event'

/** Типи компонентів для профілювання */
export type ProfilerComponentType = 'component' | 'template'

/** Інформація про компонент для профілювання */
export interface ProfilerComponentInfo {
    id: string
    name: string
    type: ProfilerComponentType
    path: string
    parentId?: string
    children: string[]
    states: Record<string, any>
    storages: Record<string, any>
    settings: Record<string, any>
    props: Record<string, any>
    renderCount: number
    lastRenderTime: number
    totalRenderTime: number
    averageRenderTime: number
    createdAt: number
    updatedAt: number
    statesSize: number
    storagesSize: number
    settingsSize: number
    propsSize: number
    memoryUsage: number
    status: 'active' | 'inactive' | 'error'
    errorMessage?: string
    eventHandlers: string[]
    eventHistory: Array<{ event: string; timestamp: number; data?: any }>
    instance: any
}

/** Ієрархічна структура компонентів */
export interface ProfilerHierarchy {
    component: ProfilerComponentInfo
    children: ProfilerHierarchy[]
}

/** Статистика профілювання */
export interface ProfilerStats {
    components: {
        total: number
        active: number
        inactive: number
        errors: number
        byType: { component: number; template: number }
    }
    performance: {
        totalMemory: number
        totalRenders: number
        averageRenderTime: number
        slowestComponent: ProfilerComponentInfo | null
        fastestComponent: ProfilerComponentInfo | null
    }
    events: {
        totalEvents: number
        mostActiveComponent: ProfilerComponentInfo | null
    }
}

/**
 * Центральний менеджер профілювання UI компонентів
 */
export class ProfilerManager {
    private static instance: ProfilerManager

    /** Observable дані - тільки компоненти */
    components: Map<string, ProfilerComponentInfo> = new Map()
    hierarchy: ProfilerHierarchy[] = []
    isEnabled: boolean = false

    private eventManager: EventManager

    constructor() {
        /** Перевіряємо режим розробки */
        this.isEnabled = this.checkDevMode()

        makeObservable(this, {
            components: observable,
            hierarchy: observable,
            isEnabled: observable,
            registerComponent: action,
            unregisterComponent: action,
            updateComponent: action,
            addComponentEvent: action,
            setComponentStatus: action,
            clear: action,
            setEnabled: action,
        })

        this.eventManager = new EventManager({ context: this })

        if (this.isEnabled && typeof window !== 'undefined') {
            /** Додаємо глобальний доступ для дебагу */
            ;(window as any).__ProfilerManager = this
        }
    }

    static getInstance(): ProfilerManager {
        if (!ProfilerManager.instance) {
            ProfilerManager.instance = new ProfilerManager()
        }
        return ProfilerManager.instance
    }

    /**
     * Реєструє компонент для профілювання
     */
    registerComponent(
        id: string,
        instance: any,
        metadata: {
            name: string
            type: ProfilerComponentType
            path: string
            parentId?: string
        }
    ): void {
        if (!this.isEnabled) return

        runInAction(() => {
            const now = Date.now()
            const existing = this.components.get(id)

            const componentInfo: ProfilerComponentInfo = {
                id,
                name: metadata.name,
                type: metadata.type,
                path: metadata.path,
                parentId: metadata.parentId,
                children: existing?.children || [],
                states: this.cloneData(instance?.states || {}),
                storages: this.cloneData(instance?.storages || {}),
                settings: this.cloneData(instance?.settings || {}),
                props: this.cloneData(instance?.props || {}),
                renderCount: existing?.renderCount || 0,
                lastRenderTime: now,
                totalRenderTime: existing?.totalRenderTime || 0,
                averageRenderTime: existing?.averageRenderTime || 0,
                createdAt: existing?.createdAt || now,
                updatedAt: now,
                statesSize: this.calculateSize(instance?.states),
                storagesSize: this.calculateSize(instance?.storages),
                settingsSize: this.calculateSize(instance?.settings),
                propsSize: this.calculateSize(instance?.props),
                memoryUsage: this.calculateMemoryUsage(instance),
                status: 'active',
                eventHandlers: this.extractEventHandlers(instance),
                eventHistory: existing?.eventHistory || [],
                instance,
            }

            this.components.set(id, componentInfo)
            this.updateHierarchy()

            /** Оновлюємо батьківські зв'язки */
            if (metadata.parentId) {
                const parent = this.components.get(metadata.parentId)
                if (parent && !parent.children.includes(id)) {
                    parent.children.push(id)
                }
            }

            this.eventManager.emit('component:registered', { id, metadata })
        })
    }

    /**
     * Видаляє компонент з профілювання
     */
    unregisterComponent(id: string): void {
        if (!this.isEnabled) return

        runInAction(() => {
            const component = this.components.get(id)
            if (component) {
                /** Видаляємо з батька */
                if (component.parentId) {
                    const parent = this.components.get(component.parentId)
                    if (parent) {
                        parent.children = parent.children.filter((childId) => childId !== id)
                    }
                }

                /** Переносимо дітей до батька або робимо їх кореневими */
                component.children.forEach((childId) => {
                    const child = this.components.get(childId)
                    if (child) {
                        child.parentId = component.parentId
                        if (component.parentId) {
                            const parent = this.components.get(component.parentId)
                            if (parent && !parent.children.includes(childId)) {
                                parent.children.push(childId)
                            }
                        }
                    }
                })

                this.components.delete(id)
                this.updateHierarchy()

                this.eventManager.emit('component:unregisterred', { id })
            }
        })
    }

    /**
     * Оновлює дані компонента
     */
    updateComponent(id: string, renderTime?: number): void {
        if (!this.isEnabled) return

        runInAction(() => {
            const component = this.components.get(id)
            if (component) {
                const now = Date.now()

                /** Оновлюємо snapshot даних */
                component.states = this.cloneData(component.instance?.states || {})
                component.storages = this.cloneData(component.instance?.storages || {})
                component.settings = this.cloneData(component.instance?.settings || {})
                component.props = this.cloneData(component.instance?.props || {})

                /** Оновлюємо метрики */
                component.renderCount++
                component.lastRenderTime = now
                component.updatedAt = now

                if (renderTime !== undefined) {
                    component.totalRenderTime += renderTime
                    component.averageRenderTime = component.totalRenderTime / component.renderCount
                }

                /** Оновлюємо розміри */
                component.statesSize = this.calculateSize(component.instance?.states)
                component.storagesSize = this.calculateSize(component.instance?.storages)
                component.settingsSize = this.calculateSize(component.instance?.settings)
                component.propsSize = this.calculateSize(component.instance?.props)
                component.memoryUsage = this.calculateMemoryUsage(component.instance)

                /** Оновлюємо обробники подій */
                component.eventHandlers = this.extractEventHandlers(component.instance)

                this.eventManager.emit('component:updated', { id, renderTime })
            }
        })
    }

    /**
     * Додає подію в історію компонента
     */
    addComponentEvent(id: string, event: string, data?: any): void {
        if (!this.isEnabled) return

        runInAction(() => {
            const component = this.components.get(id)
            if (component) {
                component.eventHistory.push({
                    event,
                    timestamp: Date.now(),
                    data: data ? this.cloneData(data) : undefined,
                })

                /** Обмежуємо історію останніми 100 подіями */
                if (component.eventHistory.length > 100) {
                    component.eventHistory = component.eventHistory.slice(-100)
                }
            }
        })
    }

    /**
     * Встановлює статус компонента
     */
    setComponentStatus(id: string, status: ProfilerComponentInfo['status'], errorMessage?: string): void {
        if (!this.isEnabled) return

        runInAction(() => {
            const component = this.components.get(id)
            if (component) {
                component.status = status
                component.errorMessage = errorMessage
                component.updatedAt = Date.now()
            }
        })
    }

    /**
     * Оновлює рендер шаблону (для зворотної сумісності з UIManager)
     */
    updateTemplateRender(id: string, renderTime: number): void {
        this.updateComponent(id, renderTime)
    }

    /**
     * Реєструє шаблон (для зворотної сумісності з UIManager)
     */
    registerTemplate(templateInfo: {
        id: string
        name: string
        path: string
        props: Record<string, any>
        slots: string[]
        dependencies: string[]
        size: number
    }): void {
        /** Для шаблонів перевіряємо, чи вони вже зареєстровані через registerComponent */
        if (!this.components.has(templateInfo.id)) {
            /** Шаблон буде зареєстрований пізніше через registerComponent */
        }
    }

    /** =============== Отримання даних =============== */

    /**
     * Отримує всі компоненти
     */
    getAllComponents(): ProfilerComponentInfo[] {
        return Array.from(this.components.values())
    }

    /**
     * Отримує компонент за ID
     */
    getComponent(id: string): ProfilerComponentInfo | undefined {
        return this.components.get(id)
    }

    /**
     * Отримує поточний активний компонент (для backwards compatibility)
     */
    getCurrentPageComponent(): ProfilerComponentInfo | undefined {
        const activeComponents = Array.from(this.components.values()).filter((c) => c.status === 'active')
        return activeComponents.sort((a, b) => b.lastRenderTime - a.lastRenderTime)[0]
    }

    /**
     * Отримує ієрархічну структуру компонентів
     */
    getHierarchy(): ProfilerHierarchy[] {
        return this.hierarchy
    }

    /**
     * Отримує компоненти за типом
     */
    getComponentsByType(type: ProfilerComponentType): ProfilerComponentInfo[] {
        return Array.from(this.components.values()).filter((c) => c.type === type)
    }

    /**
     * Шукає компоненти за назвою
     */
    searchComponents(query: string): ProfilerComponentInfo[] {
        const lowerQuery = query.toLowerCase()
        return Array.from(this.components.values()).filter(
            (c) =>
                c.name.toLowerCase().includes(lowerQuery) ||
                c.path.toLowerCase().includes(lowerQuery) ||
                c.id.toLowerCase().includes(lowerQuery)
        )
    }

    /**
     * Отримує дітей компонента
     */
    getComponentChildren(id: string): ProfilerComponentInfo[] {
        const component = this.components.get(id)
        if (!component) return []

        return component.children
            .map((childId) => this.components.get(childId))
            .filter((c) => c !== undefined) as ProfilerComponentInfo[]
    }

    /**
     * Отримує шлях до компонента (від кореня)
     */
    getComponentPath(id: string): ProfilerComponentInfo[] {
        const path: ProfilerComponentInfo[] = []
        let current = this.components.get(id)

        while (current) {
            path.unshift(current)
            current = current.parentId ? this.components.get(current.parentId) : undefined
        }

        return path
    }

    /**
     * Отримує статистику профілювання
     */
    getStats(): ProfilerStats {
        const components = Array.from(this.components.values())

        const activeComponents = components.filter((c) => c.status === 'active')
        const inactiveComponents = components.filter((c) => c.status === 'inactive')
        const errorComponents = components.filter((c) => c.status === 'error')

        const componentsByType = {
            component: components.filter((c) => c.type === 'component').length,
            template: components.filter((c) => c.type === 'template').length,
        }

        const totalMemory = components.reduce((sum, c) => sum + c.memoryUsage, 0)
        const totalRenders = components.reduce((sum, c) => sum + c.renderCount, 0)
        const averageRenderTime =
            components.length > 0 ? components.reduce((sum, c) => sum + c.averageRenderTime, 0) / components.length : 0

        const componentsWithRenders = components.filter((c) => c.renderCount > 0)
        const slowestComponent =
            componentsWithRenders.length > 0
                ? componentsWithRenders.reduce((max, c) => (c.averageRenderTime > max.averageRenderTime ? c : max))
                : null

        const fastestComponent =
            componentsWithRenders.length > 0
                ? componentsWithRenders.reduce((min, c) => (c.averageRenderTime < min.averageRenderTime ? c : min))
                : null

        const totalEvents = components.reduce((sum, c) => sum + c.eventHistory.length, 0)
        const mostActiveComponent =
            components.length > 0
                ? components.reduce((max, c) => (c.eventHistory.length > max.eventHistory.length ? c : max))
                : null

        return {
            components: {
                total: components.length,
                active: activeComponents.length,
                inactive: inactiveComponents.length,
                errors: errorComponents.length,
                byType: componentsByType,
            },
            performance: {
                totalMemory,
                totalRenders,
                averageRenderTime,
                slowestComponent,
                fastestComponent,
            },
            events: {
                totalEvents,
                mostActiveComponent,
            },
        }
    }

    /** =============== Управління =============== */

    /**
     * Вмикає/вимикає профілювання
     */
    setEnabled(enabled: boolean): void {
        runInAction(() => {
            this.isEnabled = enabled
        })
    }

    /**
     * Очищує всі дані профілювання
     */
    clear(): void {
        runInAction(() => {
            this.components.clear()
            this.hierarchy = []
        })
    }

    /** =============== Приватні методи =============== */

    /**
     * Перевіряє чи працюємо в режимі розробки
     */
    private checkDevMode(): boolean {
        try {
            const env = (import.meta as any).env
            return (
                env?.DEV === true ||
                env?.MODE === 'development' ||
                (typeof window !== 'undefined' && window.location.hostname === 'localhost')
            )
        } catch {
            return false
        }
    }

    /**
     * Клонує дані для snapshot
     */
    private cloneData(data: any): any {
        try {
            return JSON.parse(JSON.stringify(data))
        } catch {
            return String(data)
        }
    }

    /**
     * Обчислює розмір даних в байтах
     */
    private calculateSize(data: any): number {
        try {
            return new Blob([JSON.stringify(data)]).size
        } catch {
            return 0
        }
    }

    /**
     * Обчислює загальне використання пам'яті
     */
    private calculateMemoryUsage(instance: any): number {
        if (!instance) return 0

        return (
            this.calculateSize(instance.states) +
            this.calculateSize(instance.storages) +
            this.calculateSize(instance.settings) +
            this.calculateSize(instance.props)
        )
    }

    /**
     * Витягує список обробників подій
     */
    private extractEventHandlers(instance: any): string[] {
        const handlers: string[] = []

        if (instance?.getEventManager && typeof instance.getEventManager === 'function') {
            try {
                const eventManager = instance.getEventManager()
                if (eventManager && typeof eventManager.getEventList === 'function') {
                    const events = eventManager.getEventList()
                    handlers.push(...events)
                }
            } catch {
                /** Ігноруємо помилки */
            }
        }

        return handlers
    }

    /**
     * Оновлює ієрархічну структуру компонентів
     */
    private updateHierarchy(): void {
        const components = Array.from(this.components.values())
        const rootComponents = components.filter((c) => !c.parentId)

        this.hierarchy = rootComponents.map((component) => this.buildHierarchyNode(component, components))
    }

    /**
     * Фільтрує компоненти за статусом
     */
    filterByStatus(status: 'active' | 'inactive' | 'error'): ProfilerComponentInfo[] {
        return Array.from(this.components.values()).filter((c) => c.status === status)
    }

    /**
     * Отримує топ N найповільніших компонентів
     */
    getSlowestComponents(limit: number = 10): ProfilerComponentInfo[] {
        return Array.from(this.components.values())
            .sort((a, b) => b.averageRenderTime - a.averageRenderTime)
            .slice(0, limit)
    }

    /**
     * Отримує топ N найбільш активних компонентів (за подіями)
     */
    getMostActiveComponents(limit: number = 10): ProfilerComponentInfo[] {
        return Array.from(this.components.values())
            .sort((a, b) => b.eventHistory.length - a.eventHistory.length)
            .slice(0, limit)
    }

    /**
     * Отримує топ N компонентів з найбільшим використанням пам'яті
     */
    getMemoryHeavyComponents(limit: number = 10): ProfilerComponentInfo[] {
        return Array.from(this.components.values())
            .sort((a, b) => b.memoryUsage - a.memoryUsage)
            .slice(0, limit)
    }

    /**
     * Експортує дані профілювання у JSON
     */
    exportToJSON(): string {
        const data = {
            timestamp: new Date().toISOString(),
            stats: this.getStats(),
            components: Array.from(this.components.values()).map((c) => ({
                ...c,
                instance: undefined, // Видаляємо instance для серіалізації
            })),
            hierarchy: this.hierarchy,
        }
        return JSON.stringify(data, null, 2)
    }

    /**
     * Імпортує дані профілювання з JSON (для аналізу)
     */
    importFromJSON(json: string): void {
        try {
            const data = JSON.parse(json)
            if (data.components && Array.isArray(data.components)) {
                runInAction(() => {
                    this.components.clear()
                    data.components.forEach((comp: ProfilerComponentInfo) => {
                        this.components.set(comp.id, comp)
                    })
                    this.updateHierarchy()
                })
            }
        } catch (error) {
            console.error('Помилка імпорту даних профілювання:', error)
        }
    }

    /**
     * Очищує стару історію подій (залишає тільки останні N подій)
     */
    cleanupEventHistory(keepLast: number = 100): void {
        runInAction(() => {
            this.components.forEach((component) => {
                if (component.eventHistory.length > keepLast) {
                    component.eventHistory = component.eventHistory.slice(-keepLast)
                }
            })
        })
    }

    /**
     * Отримує статистику за період часу
     */
    getTimeRangeStats(
        fromTimestamp: number,
        toTimestamp: number
    ): {
        components: number
        renders: number
        events: number
    } {
        let renders = 0
        let events = 0

        this.components.forEach((comp) => {
            if (comp.createdAt >= fromTimestamp && comp.createdAt <= toTimestamp) {
                renders += comp.renderCount
                events += comp.eventHistory.filter(
                    (e) => e.timestamp >= fromTimestamp && e.timestamp <= toTimestamp
                ).length
            }
        })

        return {
            components: this.components.size,
            renders,
            events,
        }
    }

    /**
     * Знаходить компоненти з помилками
     */
    getComponentsWithErrors(): ProfilerComponentInfo[] {
        return this.filterByStatus('error')
    }

    /**
     * Отримує середній час життя компонентів
     */
    getAverageComponentLifetime(): number {
        const now = Date.now()
        const lifetimes = Array.from(this.components.values()).map((c) => now - c.createdAt)
        return lifetimes.length > 0 ? lifetimes.reduce((sum, time) => sum + time, 0) / lifetimes.length : 0
    }

    /**
     * Генерує звіт про продуктивність
     */
    generatePerformanceReport(): {
        summary: string
        slowestComponents: ProfilerComponentInfo[]
        memoryHeavyComponents: ProfilerComponentInfo[]
        mostActiveComponents: ProfilerComponentInfo[]
        errorsCount: number
    } {
        const stats = this.getStats()
        const slowest = this.getSlowestComponents(5)
        const memoryHeavy = this.getMemoryHeavyComponents(5)
        const mostActive = this.getMostActiveComponents(5)
        const errors = this.getComponentsWithErrors()

        const summary = `
Звіт про продуктивність UI компонентів
========================================
Загальна кількість компонентів: ${stats.components.total}
Активних: ${stats.components.active}
З помилками: ${stats.components.errors}

Продуктивність:
- Середній час рендеру: ${stats.performance.averageRenderTime.toFixed(2)} ms
- Загальна кількість рендерів: ${stats.performance.totalRenders}
- Загальне використання пам'яті: ${(stats.performance.totalMemory / 1024 / 1024).toFixed(2)} MB

Найповільніший компонент: ${stats.performance.slowestComponent?.name || 'N/A'}
Найактивніший компонент: ${stats.events.mostActiveComponent?.name || 'N/A'}
        `.trim()

        return {
            summary,
            slowestComponents: slowest,
            memoryHeavyComponents: memoryHeavy,
            mostActiveComponents: mostActive,
            errorsCount: errors.length,
        }
    }

    /**
     * Будує вузол ієрархії
     */
    private buildHierarchyNode(
        component: ProfilerComponentInfo,
        allComponents: ProfilerComponentInfo[]
    ): ProfilerHierarchy {
        const children = component.children
            .map((childId) => allComponents.find((c) => c.id === childId))
            .filter((child) => child !== undefined)
            .map((child) => this.buildHierarchyNode(child!, allComponents))

        return {
            component,
            children,
        }
    }
}

/**
 * Експортуємо singleton instance
 */
export const profilerManager = ProfilerManager.getInstance()
